import { useMemo, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { format, startOfWeek, addDays, isToday, isSameDay } from 'date-fns';
import { Plus, Check, X, CalendarRange } from 'lucide-react';
import { Task } from '@/types/task';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useWeekTour } from '@/hooks/usePageTours';

interface WeekViewProps {
  tasks: Task[];
  weekStart: Date;
  onAddTask: (content: string, day: string) => Promise<void>;
  onToggleTask: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onReorderTasks: (tasks: Task[]) => void;
}

export function WeekView({
  tasks,
  weekStart,
  onAddTask,
  onToggleTask,
  onUpdateTask,
  onReorderTasks,
}: WeekViewProps) {
  const [quickAddDay, setQuickAddDay] = useState<string | null>(null);
  const [quickAddContent, setQuickAddContent] = useState('');
  const isMobile = useIsMobile();
  const todayRef = useRef<HTMLDivElement>(null);
  const hasScrolledToToday = useRef(false);
  const { showBanner, dismiss } = useWeekTour();

  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll so current day card is at the top when opening the view.
  // Delay until after layout settles (framer-motion animations, etc.) for consistent positioning.
  const scrollTodayIntoView = useCallback(() => {
    const el = todayRef.current;
    if (!el || hasScrolledToToday.current) return;
    hasScrolledToToday.current = true;
    // Clear any pending scroll (e.g. from previous effect run)
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    // Wait for layout: framer-motion last card animates at ~300ms; add buffer so scroll is reliable
    scrollTimeoutRef.current = setTimeout(() => {
      scrollTimeoutRef.current = null;
      if (todayRef.current?.isConnected) {
        todayRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }, 350);
  }, []);

  const setTodayRef = useCallback((node: HTMLDivElement | null) => {
    (todayRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    if (node) scrollTodayIntoView();
  }, [scrollTodayIntoView]);

  // Reset scroll flag when week changes so we scroll to current day in the new week
  useEffect(() => {
    hasScrolledToToday.current = false;
    scrollTodayIntoView();
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [scrollTodayIntoView, weekStart]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const tasksByDay = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    weekDays.forEach((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      grouped[dateStr] = tasks
        .filter((t) => t.day === dateStr)
        .sort((a, b) => a.order_index - b.order_index);
    });
    return grouped;
  }, [tasks, weekDays]);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceDay = source.droppableId;
    const destDay = destination.droppableId;
    const taskId = result.draggableId;

    if (sourceDay === destDay && source.index === destination.index) return;

    // Find the task
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    // Update task day if moved to different day
    if (sourceDay !== destDay) {
      onUpdateTask(taskId, { day: destDay });
    }

    // Build updated destination tasks list
    const sourceTasks = [...(tasksByDay[sourceDay] || [])];
    const destTasks = sourceDay === destDay 
      ? sourceTasks 
      : [...(tasksByDay[destDay] || [])];
    
    // Remove from source
    const sourceIndex = sourceTasks.findIndex((t) => t.id === taskId);
    if (sourceIndex !== -1) {
      sourceTasks.splice(sourceIndex, 1);
    }
    
    // If cross-day move, also remove from destTasks if it was somehow there
    if (sourceDay !== destDay) {
      const destIndex = destTasks.findIndex((t) => t.id === taskId);
      if (destIndex !== -1) {
        destTasks.splice(destIndex, 1);
      }
    }
    
    // Insert at destination
    const movedTask = { ...task, day: destDay };
    destTasks.splice(destination.index, 0, movedTask);
    
    // Build the updated tasks array
    const allUpdatedTasks = tasks.map((t) => {
      // The moved task gets new day and order
      if (t.id === taskId) {
        return { ...t, day: destDay, order_index: destination.index };
      }
      // Tasks in dest day get updated order indices
      const destIndex = destTasks.findIndex((dt) => dt.id === t.id);
      if (destIndex !== -1 && t.day === destDay) {
        return { ...t, order_index: destIndex };
      }
      // Tasks in source day (if cross-day) get updated order indices
      if (sourceDay !== destDay) {
        const srcIndex = sourceTasks.findIndex((st) => st.id === t.id);
        if (srcIndex !== -1 && t.day === sourceDay) {
          return { ...t, order_index: srcIndex };
        }
      }
      return t;
    });

    onReorderTasks(allUpdatedTasks);
  };

  const handleQuickAdd = async (day: string) => {
    if (!quickAddContent.trim()) return;
    await onAddTask(quickAddContent.trim(), day);
    setQuickAddContent('');
    setQuickAddDay(null);
  };

  return (
    <div className="space-y-4">
      {/* First-visit banner */}
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center justify-between gap-3"
        >
          <p className="text-sm font-medium text-foreground">Your schedule for the week</p>
          <Button variant="ghost" size="icon" onClick={dismiss} className="shrink-0 h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </motion.div>
      )}

      {/* Header - compact on mobile so day cards start right below nav */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(isMobile && "space-y-0.5")}
      >
        <h1 className={cn(
          "font-semibold tracking-tight",
          isMobile ? "text-lg" : "text-2xl"
        )}>
          Week Overview
        </h1>
        <p className={cn(
          "text-muted-foreground",
          isMobile ? "text-xs" : "text-sm"
        )}>
          {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </p>
      </motion.div>

      {/* Empty state: no tasks scheduled this week */}
      {tasks.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12 rounded-xl border border-border/50 bg-card"
        >
          <div className="w-12 h-12 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <CalendarRange className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">Your week is clear. Time to plan!</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Add tasks to any day below</p>
        </motion.div>
      )}

      {/* Week Grid */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
          {weekDays.map((day, dayIndex) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayTasks = tasksByDay[dateStr] || [];
            const completedCount = dayTasks.filter((t) => t.completed).length;
            const isCurrentDay = isToday(day);

            return (
              <motion.div
                key={dateStr}
                data-date={dateStr}
                ref={isCurrentDay ? setTodayRef : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: dayIndex * 0.05 }}
                className={cn(
                  "rounded-xl border border-border/50 bg-card p-3 task-shadow",
                  "min-h-[200px] flex flex-col",
                  isCurrentDay && "ring-2 ring-primary/30 border-primary/50",
                  isCurrentDay && "scroll-mt-[4rem]"
                )}
              >
                {/* Day Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className={cn(
                      "text-xs font-medium uppercase tracking-wider",
                      isCurrentDay ? "text-primary" : "text-muted-foreground"
                    )}>
                      {format(day, 'EEE')}
                    </p>
                    <p className={cn(
                      "text-lg font-semibold",
                      isCurrentDay && "text-primary"
                    )}>
                      {format(day, 'd')}
                    </p>
                  </div>
                  {dayTasks.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {completedCount}/{dayTasks.length}
                    </span>
                  )}
                </div>

                {/* Tasks */}
                <Droppable droppableId={dateStr}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={cn(
                        "flex-1 space-y-1.5 min-h-[60px] rounded-lg p-1 -m-1",
                        "transition-colors duration-200",
                        snapshot.isDraggingOver && "bg-accent/50"
                      )}
                    >
                      {dayTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => {
                            // Only disable transition during drag, NOT during drop animation
                            const shouldDisableTransition = snapshot.isDragging && !snapshot.isDropAnimating;
                            return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                transition: shouldDisableTransition ? 'none' : undefined,
                              }}
                              className={cn(
                                "flex items-start gap-2 p-2 rounded-lg bg-background/80",
                                "border border-border/30 text-xs cursor-grab active:cursor-grabbing",
                                "transition-shadow transition-colors duration-150",
                                snapshot.isDragging && "shadow-lg ring-2 ring-primary/20",
                                task.completed && "opacity-50"
                              )}
                            >
                              <button
                                onClick={() => onToggleTask(task.id)}
                                className={cn(
                                  "flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center mt-0.5",
                                  "transition-all duration-150",
                                  task.completed
                                    ? "bg-success border-success text-success-foreground"
                                    : "border-muted-foreground/30 hover:border-primary"
                                )}
                              >
                                {task.completed && <Check className="h-2.5 w-2.5" />}
                              </button>
                              <span className={cn(
                                "break-words line-clamp-2",
                                task.completed && "line-through text-muted-foreground"
                              )}>
                                {task.content}
                              </span>
                            </div>
                          );}}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

                {/* Quick Add */}
                {quickAddDay === dateStr ? (
                  <div className="mt-2 flex gap-1">
                    <Input
                      autoFocus={!isMobile}
                      value={quickAddContent}
                      onChange={(e) => setQuickAddContent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleQuickAdd(dateStr);
                        if (e.key === 'Escape') setQuickAddDay(null);
                      }}
                      placeholder="Task..."
                      className="h-7 text-xs"
                    />
                    <Button
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => handleQuickAdd(dateStr)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 h-7 text-xs text-muted-foreground hover:text-foreground w-full"
                    onClick={() => setQuickAddDay(dateStr)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                )}
              </motion.div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
